import { format } from 'date-fns';
import type { ChiTietPhieuNhapKho, ChiTietPhieuNhapKhoInput, PhieuNhapKho, PhieuNhapKhoInput } from '@/types/schemas';
import { transformDocumentNumber } from '@/components/custom/erp';
import { roundNumber } from '@/lib/formatUtils';
import { calculateTotals } from './calc-util';
import type { FormSchema } from '../schemas';
import { FormFieldState } from '../hooks';

const roundToTwoDecimals = (num: number): number => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

const toUuid = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'uuid' in value && typeof value.uuid === 'string') return value.uuid;
  return '';
};

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (detailRows: ChiTietPhieuNhapKho[]): ChiTietPhieuNhapKhoInput[] => {
  return detailRows.map((row, index: number) => ({
    line: index + 1,
    ma_vt: row.ma_vt_data?.uuid || toUuid(row.ma_vt),
    dvt: toUuid(row.dvt) || row.ma_vt_data?.dvt || '',
    // ten_dvt: row.ma_vt_data?.ten_dvt || row.ten_dvt || '',

    ma_kho: row.ma_kho_data?.uuid || row.ma_vt_data?.ma_kho_data?.uuid || toUuid(row.ma_kho),
    ten_kho: row.ma_kho_data?.ten_kho || row.ma_vt_data?.ma_kho_data?.ten_kho || row.ten_kho || '',

    ma_lo: row.ma_lo_data?.uuid || row.ma_lo || '',
    ten_lo: row.ma_lo_data?.ten_lo || row.ten_lo || '',
    lo_yn: row.lo_yn || false,

    ma_vi_tri: row.ma_vi_tri_data?.uuid || row.ma_vi_tri || '',
    ten_vi_tri: row.ma_vi_tri_data?.ten_vi_tri || row.ten_vi_tri || '',
    vi_tri_yn: row.vi_tri_yn || false,

    he_so: row.he_so || 1,
    qc_yn: row.qc_yn || false,
    so_luong: roundNumber(row.so_luong, 6),
    pn_tb: row.pn_tb || true,

    gia_nt: roundNumber(row.gia_nt, 6),
    tien_nt: roundNumber(row.tien_nt, 6),
    gia: roundNumber(row.gia, 6),
    tien: roundNumber(row.tien, 6),

    tk_vt: row.tk_vt_data?.uuid || '',
    ma_nx: row.ma_nx_data?.uuid || row.ma_nx || '',
    tk_du: row.tk_du_data?.uuid || row.tk_du || '',

    ma_bp: row.ma_bp_data?.uuid || toUuid(row.ma_bp),
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || '',
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || '',
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || '',
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || '',
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || '',
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || '',
    ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || '',
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || '',

    sl_pn: row.sl_pn || 0,
    id_pn: row.id_pn || 0,
    line_pn: row.line_pn || 0,
    id_sx1: row.id_sx1 || 0,
    line_sx1: row.line_sx1 || 0
  }));
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param entityUnit - Entity unit information
 * @param isEdit - Boolean indicating whether the form is in edit mode
 * @param fromInvoice - Object containing invoice info if created from invoice
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: FormSchema,
  state: FormFieldState,
  detailRows: ChiTietPhieuNhapKho[] = [],
  entityUnit: { uuid?: string } | null | undefined,
  isEdit: boolean = false
): PhieuNhapKhoInput => {
  const chi_tiet = transformDetailRows(detailRows);
  const totals = calculateTotals(detailRows);

  const baseData = {
    unit_id: entityUnit?.uuid || '',
    ma_ngv: data.ma_ngv || '5',
    ma_gd: data.ma_gd || '',
    ma_kh: state.khachHang?.uuid || data.ma_kh || '',
    dien_giai: data.dien_giai || '',

    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu),
    ngay_ct: data.ngay_ct || '',
    ngay_lct: data.ngay_ct || '',
    ma_nt: state.ngoaiTe?.uuid || data.ma_nt || '',
    ty_gia: roundToTwoDecimals(parseFloat(String(data.ty_gia ?? 1)) || 1),
    status: data.status || '0',
    transfer_yn: data.transfer_yn || false,
    id_progress: 0,

    t_so_luong: roundNumber(totals.t_so_luong, 6),
    t_tien_nt: totals.t_tien_nt,
    t_tien: totals.t_tien,
    action: 'create',

    chi_tiet
  };

  return baseData;
};

export function transformDataFromHoaDon(hoaDonData: PhieuNhapKho & { chi_tiet_hoa_don?: ChiTietPhieuNhapKho[] }) {
  const chi_tiet_data = (hoaDonData.chi_tiet_hoa_don || []).map((item, index: number) => ({
    line: index + 1,
    ma_vt: item.ma_vt_data?.uuid || item.ma_vt || '',
    ma_vt_data: item.ma_vt_data,
    dvt: item.dvt || item.dvt_data?.uuid || item.ma_vt_data?.dvt || '',
    dvt_data: item.dvt_data || null,

    ma_kho_data: item.ma_kho_data,
    ma_kho: item.ma_kho_data?.uuid || item.ma_vt_data?.ma_kho_data?.uuid || item.ma_kho || '',
    ten_kho: item.ma_kho_data?.ten_kho || item.ma_vt_data?.ma_kho_data?.ten_kho || item.ten_kho || '',

    tk_vt_data: item.tk_vt_data,
    tk_du_data: hoaDonData.tk_data,

    ma_bp_data: item.ma_bp_data,
    ma_vv_data: item.ma_vv_data,
    ma_hd_data: item.ma_hd_data,
    ma_dtt_data: item.ma_dtt_data,
    ma_ku_data: item.ma_ku_data,
    ma_phi_data: item.ma_phi_data,
    ma_sp_data: item.ma_sp_data,
    ma_lsx_data: item.ma_lsx_data,
    ma_cp0_data: item.ma_cp0_data,

    so_luong: roundNumber(item.so_luong, 6),
    gia_nt: roundNumber(item.gia_nt0, 6),
    tien_nt: roundNumber(item.tien_nt0, 6),
    gia: roundNumber(item.gia, 6),
    tien: roundNumber(item.tien, 6),

    dien_giai: item.dien_giai || hoaDonData.dien_giai || ''
  }));

  return {
    ma_gd: 'NB',
    ngay_lct: hoaDonData.ngay_ct,
    ngay_ct: hoaDonData.ngay_ct,
    dien_giai: hoaDonData.dien_giai || `Nhập kho từ hoá đơn: ${hoaDonData.so_ct}`,
    ma_nt: hoaDonData.ma_nt,
    ty_gia: hoaDonData.ty_gia,

    ma_kh: hoaDonData.ma_kh_data?.uuid,
    ma_kh_data: hoaDonData.ma_kh_data || null,

    status: hoaDonData.status,
    tu_dong_tao: true,
    nguon_hoa_don_id: hoaDonData.uuid,
    chi_tiet_data
  };
}
